import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FileText, User, LogOut } from 'lucide-react';
import { setSessionExpiredHandler } from '../../shared/lib/axios';
import { SessionRenewalModal } from '../../shared/components/SessionRenewalModal';
import type { User as AuthUser } from '../../shared/lib/auth';

interface PrestadorLayoutProps {
  children: React.ReactNode;
  user: AuthUser;
  onSignOut?: () => void;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}

const NAV_ITEMS: NavItem[] = [
  {
    id: 'cadastro',
    label: 'Meu Cadastro',
    icon: <User className="w-5 h-5" />,
    path: '/prestador/cadastro',
  },
  {
    id: 'contratos',
    label: 'Meus Contratos',
    icon: <FileText className="w-5 h-5" />,
    path: '/prestador/contratos',
  },
];

export const PrestadorLayout: React.FC<PrestadorLayoutProps> = ({
  children,
  user,
  onSignOut,
}) => {
  const location = useLocation();
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    setSessionExpiredHandler(() => setSessionExpired(true));
    return () => setSessionExpiredHandler(() => {});
  }, []);

  const activeId = NAV_ITEMS.find((item) =>
    location.pathname.startsWith(item.path),
  )?.id;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <span className="text-xl font-bold text-gray-900">Portal do Prestador PJ</span>
        </div>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-600">{user.name}</span>
          {onSignOut && (
            <button
              onClick={onSignOut}
              className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700"
            >
              <LogOut className="w-4 h-4" />
              <span>Sair</span>
            </button>
          )}
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <nav className="w-56 bg-white border-r border-gray-200 pt-4">
          <ul className="space-y-1 px-2">
            {NAV_ITEMS.map((item) => {
              const isActive = item.id === activeId;
              return (
                <li key={item.id}>
                  <Link
                    to={item.path}
                    className={`flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Main content */}
        <main className="flex-1 p-6">{children}</main>
      </div>

      <SessionRenewalModal
        isOpen={sessionExpired}
        onRenewed={() => setSessionExpired(false)}
        onSignOut={() => {
          setSessionExpired(false);
          onSignOut?.();
        }}
      />
    </div>
  );
};
